import { CheckCircleFilled } from "@ant-design/icons";
import { DataImport, DataSource, DataSourceType } from "@coeff/api";
import { COLORS } from "@coeff/utils";
import React, { useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { FlexColumn, FlexRow } from "../../../common";
import { Button } from "../../../common/Button";
import { Card } from "../../../common/Card";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { IPWhitelistInfoPopup } from "../../../common/IPWhitelistInfoPopup";
import { Modal } from "../../../common/Modal";
import { BaseAuthorizeLocationState, useAuthContext } from "../../context/AuthContext";
import { pluralize } from "../../utils";

import { LookerViews } from "./constants";

const StyledSubSection = styled.div`
  text-align: center;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
`;

export const StyledAnchor = styled.a<{ fontWeight?: string | number }>`
  color: ${COLORS.coeblue4};
  font-weight: ${({ fontWeight }) => fontWeight || 600};
  font-size: 14px;
  line-height: 18px;
  :hover {
    color: ${COLORS.coeblue4};
  }
`;

export const getLookerBaseUrl = (url: string) =>
  url?.replace(/^https?:\/\//, "").replace(/(:\d+)?\/?$/, "");

export type LookerConnectionInfo = {
  url: string;
  clientId: string;
  clientSecret: string;
};

export interface AuthorizeLookerLocationState extends BaseAuthorizeLocationState {
  redirect?: string;
  redirectOnBack?: string;
  dataImport?: DataImport;
  dataSource?: DataSource;
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  dataImportRecipeNavigationProps?: any;
}

export const SecondaryMessage: React.FC<{
  title: string;
  subtitle: string;
  step: LookerViews;
  onNavigate: (step: LookerViews, options?: { replace: boolean }) => void;
}> = ({ title, subtitle, step, onNavigate }) => {
  return (
    <StyledSubSection>
      <Typography
        color="textSecondary"
        fontWeight={600}
        fontSize={14}
        lineHeight={"18px"}
        style={{
          textAlign: "center",
          marginTop: 10,
        }}
      >
        {title}
      </Typography>
      <StyledAnchor
        onClick={(event: React.MouseEvent) => {
          event.preventDefault();
          onNavigate(step, { replace: true });
        }}
        target="_blank"
      >
        {subtitle}
      </StyledAnchor>
    </StyledSubSection>
  );
};

export const HelperMessage: React.FC = () => {
  return (
    <Card className="coeblue" style={{ padding: 12 }}>
      <div style={{ textAlign: "center" }}>
        <Typography fontWeight={700} color="textPrimary" align="center">
          Need help?
        </Typography>
        <StyledAnchor
          href={
            "https://help.coefficient.io/hc/en-us/articles/17969072042907-Looker#h_01HH5QX5X3Y6JZZ431ZGF09M0B"
          }
          target="_blank"
          fontWeight={"normal"}
        >
          Watch a step-by-step video guide
        </StyledAnchor>
      </div>
    </Card>
  );
};

export const Errors: React.FC<{
  dataSource?: DataSource;
  error?: {
    title: string;
    message: string;
    secondaryMessage?: string;
    actionText?: string;
  };
  readOnly: boolean;
}> = ({ error, readOnly, dataSource }) => {
  const [showIPWhiteListInfoPopup, setShowIPWhiteListInfoPopup] = useState<boolean>(false);

  return (
    <>
      {error && (
        <div style={{ margin: 16 }}>
          <ErrorMessage
            title={error.title}
            message={error.message}
            actionText={error.actionText}
            screen="AuthorizeLooker"
            dataSourceType={DataSourceType.Looker}
            noMargin
            action={() => setShowIPWhiteListInfoPopup(true)}
          />
        </div>
      )}

      {showIPWhiteListInfoPopup && (
        <IPWhitelistInfoPopup
          dataSourceType="looker"
          setShowIPWhiteListInfoPopup={setShowIPWhiteListInfoPopup}
        />
      )}
    </>
  );
};

export const getMessageContent: React.FC<{ title: string; message: string }> = ({
  title,
  message,
}) => {
  return (
    <FlexRow style={{ alignItems: "start" }}>
      <CheckCircleFilled style={{ color: COLORS.green2 }} />
      <FlexColumn style={{ alignItems: "start" }} gap={0}>
        <Typography fontWeight={700} align="left">
          {title}
        </Typography>
        <Typography align="left">{message}</Typography>
      </FlexColumn>
    </FlexRow>
  );
};

export const SupportAssistanceModal: React.FC<{
  onHideSupport: () => void;
}> = ({ onHideSupport }) => {
  const [showSupport, setShowSupport] = useState(true);

  const { openSupportChat } = useAuthContext();

  const onSupportClick = () => openSupportChat?.();

  return (
    <Modal
      visible={showSupport}
      onCancel={() => {
        setShowSupport(false);
        onHideSupport();
      }}
      resetLineHeight
      centered
    >
      <Typography fontWeight={600} fontSize={18} lineHeight={"20px"}>
        Can’t connect?
      </Typography>
      <br />
      <Typography fontSize={16} fontWeight={500} lineHeight={"20px"} color="textSecondary">
        We can help! Message us now to help you connect to Looker.
      </Typography>
      <br />

      <FlexRow style={{ justifyContent: "space-between", marginTop: 16 }}>
        <Button type="primary" style={{ width: "100%" }} onClick={onSupportClick}>
          Send us a message
        </Button>
      </FlexRow>
    </Modal>
  );
};

export const getDescriptionText = (dataSource: DataSource | undefined) => {
  const numOwnDataImportsForSource: number = dataSource?.data_source_stats?.num_data_imports ?? 0;

  const numSharedDataImportsForSource: number =
    dataSource?.data_source_stats?.num_shared_data_imports ?? 0;

  return (
    pluralize("import", numOwnDataImportsForSource + numSharedDataImportsForSource, true) +
    " using this connection"
  );
};

export function formatOuathErrorMessage(message: string): string {
  const emailUrlRegex = /"\S+@\S+\s\(URL:[^\)]+\)"/;
  return message.replace(emailUrlRegex, "").trim();
}
